import { ElMessageBox } from "element-plus";

/**
 * 版本检测模块
 *
 * 工作原理：
 * 1. 当前版本号在构建时通过 vite-plugin-version 注入到 index.html 的 <meta name="app-version">
 * 2. 远端版本号通过轮询 /version.json 获取（URL 带时间戳防缓存）
 * 3. 两者不一致时弹窗提示用户更新
 * 4. 用户确认后强制刷新并清理历史记录，避免回退到老页面
 *
 * 关于"刷新一下新、再刷新又老"的问题：
 * 根本原因是浏览器/CDN 强缓存了 index.html，导致拿到的 html 引用的还是老 JS chunk。
 * 前端版本检测是兜底方案，服务器必须对 index.html 设置 no-cache。
 */

const POLL_INTERVAL = 1 * 60 * 1000; // 轮询间隔：5 分钟

let timer = null;
let hasNotified = false; // 是否已弹窗提示过（避免重复弹窗）

/**
 * 读取当前页面注入的版本号
 */
function getCurrentVersion() {
  const meta = document.querySelector('meta[name="app-version"]');
  return meta?.getAttribute("content") || "0.0.0";
}

/**
 * 拉取远端最新版本号（带时间戳防缓存）
 */
async function getRemoteVersion() {
  // 用相对路径，确保与站点同源；URL 拼时间戳防止任何中间层缓存
  const url = `/version.json?t=${Date.now()}`;
  const res = await fetch(url, {
    cache: "no-store",
    headers: { Accept: "application/json" }
  });
  if (!res.ok) {
    throw new Error(`fetch version.json failed: ${res.status}`);
  }
  const data = await res.json();
  return data?.version || "0.0.0";
}

/**
 * 简单的语义化版本号比较
 * 仅按数字段逐段比较，非数字段按字符串比较
 * 返回：1 表示 a > b，-1 表示 a < b，0 表示相等
 */
function compareVersion(a, b) {
  if (a === b) return 0;
  const pa = String(a).split(".");
  const pb = String(b).split(".");
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = Number(pa[i]);
    const nb = Number(pb[i]);
    if (!isNaN(na) && !isNaN(nb)) {
      if (na > nb) return 1;
      if (na < nb) return -1;
    } else {
      const sa = String(pa[i] ?? "");
      const sb = String(pb[i] ?? "");
      if (sa > sb) return 1;
      if (sa < sb) return -1;
    }
  }
  return 0;
}

/**
 * 强制刷新页面并阻止回退到老页面
 *
 * 关键点：
 * 1. 使用 location.reload() 重新加载当前 URL（不新增历史记录）
 * 2. reload 前先把当前 fullPath 写入 history.replaceState，避免后退按钮回到老路径
 * 3. 监听一次 popstate，如果用户在刷新前后退，则再次前进到当前页
 */
function forceReload() {
  // 阻止后退：在 reload 前先 push 一个状态，并监听 popstate
  // 这样即使刷新过程中用户点了后退，也会被拉回当前页面
  const curHref = window.location.href;
  try {
    history.pushState(null, "", curHref);
  } catch (e) {
    /* ignore */
  }

  const onPopState = () => {
    // 用户尝试后退，立即前进回当前页，阻止回退到老页面
    history.forward();
  };
  window.addEventListener("popstate", onPopState);

  // 强制刷新（绕过缓存）
  // 现代浏览器 location.reload() 默认会重新验证缓存
  // 这里通过给 URL 临时加时间戳参数 + reload 的组合，确保拿到最新 html
  window.location.reload();
}

/**
 * 弹窗提示用户有新版本
 */
async function notifyNewVersion(remoteVersion) {
  if (hasNotified) return;
  hasNotified = true;

  try {
    await ElMessageBox.confirm(
      `检测到系统已发布新版本（v${remoteVersion}），请刷新以获取最新功能。`,
      "系统更新提示",
      {
        confirmButtonText: "立即刷新",
        cancelButtonText: "稍后",
        type: "warning",
        closeOnClickModal: false,
        closeOnPressEscape: false
      }
    );
    // 用户点击"立即刷新"
    forceReload();
  } catch (e) {
    // 用户点击"稍后"：1 分钟后再次提示
    hasNotified = false;
  }
}

/**
 * 执行一次版本检查
 */
async function checkVersion() {
  try {
    const current = getCurrentVersion();
    const remote = await getRemoteVersion();
    if (compareVersion(remote, current) > 0) {
      await notifyNewVersion(remote);
    }
  } catch (e) {
    // 静默失败，不打扰用户（如网络异常、version.json 不存在等）
    console.warn("[versionCheck] 检查版本失败:", e);
  }
}

/**
 * 启动版本检测
 * - 立即检查一次
 * - 之后每 POLL_INTERVAL 检查一次
 * - 页面从后台切回前台时也检查一次
 */
export function startVersionCheck() {
  // dev 模式不检查版本（开发时 package.json 版本号与 version.json 始终一致，检查无意义）
  if (import.meta.env.DEV) return;

  // 立即检查一次（延迟 3s，避免与首屏渲染抢资源）
  setTimeout(checkVersion, 6 * 1000);

  // 定时轮询
  timer = setInterval(checkVersion, POLL_INTERVAL);

  // 页面从后台切回前台时检查（用户切走再回来，大概率想看新版本）
  let wasHidden = false;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      wasHidden = true;
    } else if (wasHidden) {
      wasHidden = false;
      checkVersion();
    }
  });
}

/**
 * 停止版本检测
 */
export function stopVersionCheck() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

export default { startVersionCheck, stopVersionCheck };
